<template>
  <div class="w-full flex flex-col items-center">
    <!-- Work type -->
    <div
      v-if="subStep === 1"
      class="flex flex-col items-center gap-[30px] w-full space-y-7"
    >
      <h2 class="step-title">
        What best describes your work?
      </h2>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        <button
          v-for="option in WORK_OPTIONS"
          :key="option.key"
          type="button"
          class="work-card"
          :class="{ selected: surveyForm.work === option.key }"
          @click="surveyForm.work = option.key"
        >
          <component
            :is="option.icon"
            :width="20"
            :height="20"
            class="text-white shrink-0"
          />
          <div class="flex flex-col items-start gap-1 w-full">
            <BaseText
              type="label"
              size="sm"
              class="text-white text-left"
            >
              {{ option.title }}
            </BaseText>
            <BaseText
              type="body"
              size="sm"
              class="text-neutral-alpha-650 text-left"
            >
              {{ option.description }}
            </BaseText>
          </div>
        </button>
      </div>
      <button
        class="next-button"
        :class="{ disabled: !surveyForm.work }"
        :disabled="!surveyForm.work"
        @click="goToGoals"
      >
        <BaseText
          type="heading"
          size="sm"
        >
          Next
        </BaseText>
        <ChevronRightIcon
          :width="24"
          :height="24"
        />
      </button>
    </div>

    <!-- Goals -->
    <div
      v-else
      class="flex flex-col items-center gap-[30px] w-full space-y-7"
    >
      <h2 class="step-title">
        What are you trying to accomplish?
      </h2>
      <div
        class="space-y-4 w-full"
        style="max-width: 320px"
      >
        <button
          v-for="option in GOAL_OPTIONS"
          :key="option.key"
          type="button"
          class="goal-card"
          :class="{ selected: isGoalSelected(option.key) }"
          @click="toggleGoal(option.key)"
        >
          <component
            :is="option.icon"
            :width="20"
            :height="20"
            class="text-white shrink-0"
          />
          <BaseText
            type="label"
            size="sm"
            class="text-white whitespace-nowrap"
          >
            {{ option.title }}
          </BaseText>
        </button>
      </div>
      <div
        class="space-y-3 w-full"
        style="max-width: 320px"
      >
        <button
          class="next-button w-full"
          :class="{ disabled: !hasSelectedGoal }"
          :disabled="!hasSelectedGoal"
          @click="submitSurvey"
        >
          <BaseText
            type="heading"
            size="sm"
          >
            Next
          </BaseText>
          <ChevronRightIcon
            :width="24"
            :height="24"
          />
        </button>
        <BaseText
          type="heading"
          size="sm"
          class="text-white text-center"
        >
          Select all that apply
        </BaseText>
      </div>
    </div>
  </div>
</template>

<script>
import AgencyWorkIcon from '@/components/globals/Icons/OnboardIcons/AgencyWorkIcon.vue'
import BrandWorkIcon from '@/components/globals/Icons/OnboardIcons/BrandWorkIcon.vue'
import AppGameWorkIcon from '@/components/globals/Icons/OnboardIcons/AppGameWorkIcon.vue'
import AgencyDirectorySettingsIcon from '@/components/globals/Icons/SettingsIcons/AgencyDirectorySettingsIcon.vue'
import FreelancerWorkIcon from '@/components/globals/Icons/OnboardIcons/FreelancerWorkIcon.vue'
import MoreIcon from '@/components/globals/Icons/SidebarIcons/MoreIcon.vue'
import SaveIcon from '@/components/globals/Icons/SaveIcon.vue'
import CompetitorResearchIcon from '@/components/globals/Icons/OnboardIcons/CompetitorResearchIcon.vue'
import CreativeAnalyticsIcon from '@/components/globals/Icons/OnboardIcons/CreativeAnalyticsIcon.vue'
import MCPLogoIcon from '@/components/globals/Icons/MCPLogoIcon.vue'
import CreativeInspirationIcon from '@/components/globals/Icons/OnboardIcons/CreativeInspirationIcon.vue'
import ChevronRightIcon from '@/components/globals/Icons/ChevronRightIcon.vue'

export default {
  name: 'PersonalizeOnboardSection',
  components: {
    AgencyWorkIcon,
    BrandWorkIcon,
    AppGameWorkIcon,
    AgencyDirectorySettingsIcon,
    FreelancerWorkIcon,
    MoreIcon,
    SaveIcon,
    CompetitorResearchIcon,
    CreativeAnalyticsIcon,
    MCPLogoIcon,
    CreativeInspirationIcon,
    ChevronRightIcon
  },
  props: {
    initialData: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      subStep: 1,
      surveyForm: {
        work: this.initialData?.work || null,
        goals: Array.isArray(this.initialData?.goals) ? [...this.initialData.goals] : []
      },
      WORK_OPTIONS: [
        {
          title: 'Agency',
          key: 'agency',
          description: 'You run ads for multiple clients',
          icon: 'AgencyWorkIcon'
        },
        {
          title: 'Brand',
          key: 'brand',
          description: 'You sell physical products, DTC or retail',
          icon: 'BrandWorkIcon'
        },
        {
          title: 'App & Game',
          key: 'app_or_game',
          description: 'You drive installs for a mobile product',
          icon: 'AppGameWorkIcon'
        },
        {
          title: 'Software',
          key: 'software',
          description: 'You market a SaaS or B2B product',
          icon: 'AgencyDirectorySettingsIcon'
        },
        {
          title: 'Freelancer',
          key: 'freelancer',
          description: 'You handle creative for one or two clients',
          icon: 'FreelancerWorkIcon'
        },
        {
          title: 'Other',
          key: 'other',
          description: 'Something else, or a bit of everything',
          icon: 'MoreIcon'
        }
      ],
      GOAL_OPTIONS: [
        { title: 'Saving Ads', key: 'saving_ads', icon: 'SaveIcon' },
        { title: 'Competitor Research', key: 'competitor_research', icon: 'CompetitorResearchIcon' },
        { title: 'Creative Analytics & Reports', key: 'creative_analytics', icon: 'CreativeAnalyticsIcon' },
        { title: 'MCP & API', key: 'mcp_api', icon: 'MCPLogoIcon' },
        { title: 'Creative Inspiration', key: 'creative_inspiration', icon: 'CreativeInspirationIcon' }
      ]
    }
  },
  computed: {
    hasSelectedGoal () {
      return this.surveyForm.goals.length > 0
    }
  },
  methods: {
    isGoalSelected (key) {
      return this.surveyForm.goals.includes(key)
    },
    toggleGoal (key) {
      if (this.isGoalSelected(key)) {
        this.surveyForm.goals = this.surveyForm.goals.filter(goal => goal !== key)
      } else {
        this.surveyForm.goals = [...this.surveyForm.goals, key]
      }
    },
    goToGoals () {
      if (!this.surveyForm.work) return
      this.$emit('save-progress', {
        step: 'personalize',
        data: { work: this.surveyForm.work, goals: this.surveyForm.goals }
      })
      this.subStep = 2
      window.scrollTo(0, 0)
    },
    submitSurvey () {
      if (!this.hasSelectedGoal) return
      this.$emit('next', { step: 'personalize', data: this.surveyForm })
    }
  }
}
</script>

<style scoped>
.step-title {
  width: 100%;
  max-width: 480px;
  margin: 0;
  font-size: 28px;
  line-height: 36px;
  font-weight: 600;
  letter-spacing: -0.2px;
  text-align: center;
  color: #fff;
}

.work-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  width: 184px;
  height: 160px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background-color: transparent;
  text-align: left;
  transition: border-color 150ms ease-in-out, background-color 150ms ease-in-out;
}

.goal-card {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  max-width: 320px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background-color: transparent;
  text-align: left;
  transition: border-color 150ms ease-in-out, background-color 150ms ease-in-out;
}

.work-card.selected,
.goal-card.selected {
  background-color: rgba(255, 255, 255, 0.06);
  border-color: #fff;
}

.next-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 320px;
  height: 40px;
  padding: 8px;
  border-radius: 10px;
  background-color: #1F69FF;
  color: #fff;
  transition: background-color 150ms ease-in-out, color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}

.next-button.disabled,
.next-button.disabled:hover {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.32);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1);
  cursor: default;
}

@media (max-width: 767px) {
  .work-card {
    width: 100%;
  }
}
</style>
